import { useState } from 'react';
import {
  Box,
  Button,
  Circle,
  Flex,
  Icon,
  Text,
  useToast,
} from '@chakra-ui/react';
import SectionWrapper from '../../pass-templates-new/SectionWrapper';
import CustomToast from '../../../../common/CustomToast';
import { useUpdateLoyaltyBalanceMutation } from '../../../../store/redux/api/passApi';
import { CustomStampCheckIcon } from '../../../../theme/icons/CustomStampCheckIcon.js';

interface StampPassSectionProps {
  passUuid: string;
  currentStamps: number;
  totalStamps: number;
}

const StampPassSection = ({
  passUuid,
  currentStamps,
  totalStamps,
}: StampPassSectionProps) => {
  const toast = useToast();
  const [updateBalance, { isLoading }] = useUpdateLoyaltyBalanceMutation();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const isComplete = currentStamps >= totalStamps;

  const showToast = (status: 'success' | 'error', title: string) => {
    toast({
      render: (props) => (
        <CustomToast
          status={status}
          title={title}
          description=""
          onClose={props.onClose}
        />
      ),
    });
  };

  const handleStamp = async () => {
    setSuccessMessage(null);
    try {
      if (isComplete) {
        await updateBalance({ passId: passUuid, overrideBalance: 0 }).unwrap();
        setSuccessMessage('Pass has been reset to unstamped.');
      } else {
        await updateBalance({ passId: passUuid, balance: 1 }).unwrap();
        setSuccessMessage('Stamp added. This pass has been updated.');
      }
    } catch {
      showToast(
        'error',
        isComplete ? 'Failed to reset stamps' : 'Failed to add stamp'
      );
    }
  };

  return (
    <SectionWrapper
      title="Stamp pass"
      openOnLoad
      id="stamp-pass-section"
      subtitle={
        <Text fontSize="15px" color="grey">
            Add a stamp to this holder&apos;s pass, updates their wallet in real
            time
        </Text>
      }
    >
      <Box display="flex" flexDirection="column" gap={4}>
        <Text textStyle="bodyFamilyMedium" fontSize="16px">
          Stamps collected{' '}
          <Text as="span" color="#989898" fontSize="14px">
            ({currentStamps}/{totalStamps})
          </Text>
        </Text>

        <Flex gap={4} flexWrap="wrap">
          {Array.from({ length: totalStamps }, (_, i) => (
            <Circle
              key={i}
              size="42px"
              bg={
                i < currentStamps
                  ? 'linear-gradient(180deg, #FF9554 16%, #FF4935 100%)'
                  : '#E8E8E8'
              }
              color="white"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {i < currentStamps && <CustomStampCheckIcon boxSize="25px" />}
            </Circle>
          ))}
        </Flex>

        <Button
          w="full"
          variant="outline"
          borderColor="#3A4A57"
          borderWidth="1.5px"
          color="#3A4A57"
          borderRadius="5px"
          fontWeight="bold"
          fontSize="14px"
          h="40px"
          isLoading={isLoading}
          onClick={handleStamp}
          _hover={{ bg: '#F5F5F5' }}
        >
          {isComplete ? 'Stamp to reset pass' : 'Stamp pass'}
        </Button>

        {successMessage && (
          <Flex
            bg="rgba(51, 153, 0, 0.1)"
            borderRadius="5px"
            px="14px"
            py="10px"
            gap={2}
            align="center"
          >
            <CustomStampCheckIcon
              boxSize="22px"
              color="#339900"
              flexShrink={0}
            />
            {/* <Icon
              viewBox="0 0 16 16"
              boxSize="22px"
              color="#339900"
              flexShrink={0}
            >
              <path
                d="M3.5 8.5L6.5 11.5L12.5 4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </Icon> */}
            <Text
              fontSize="15px"
              color="rgba(0, 0, 0, 0.8)"
              letterSpacing="-0.165px"
            >
              {successMessage === 'Pass has been reset to unstamped.' ? (
                <>
                  Pass has been reset to <strong>unstamped</strong>.
                </>
              ) : (
                successMessage
              )}
            </Text>
          </Flex>
        )}
      </Box>
    </SectionWrapper>
  );
};

export default StampPassSection;
